import { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, Download, Maximize2, Minimize2, FileText, Image, Film, File as FileIcon } from 'lucide-react';

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function resolveUrl(fileUrl) {
  if (!fileUrl) return null;
  return fileUrl.startsWith('http') ? fileUrl : `${process.env.REACT_APP_BACKEND_URL}${fileUrl}`;
}

function detectType(fileUrl, contentType) {
  const ct = contentType || '';
  const url = fileUrl || '';
  return {
    isImage: ct.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|svg|heic)$/i.test(url),
    isPdf: ct === 'application/pdf' || /\.pdf$/i.test(url),
    isVideo: ct.startsWith('video/') || /\.(mp4|mov|webm|avi)$/i.test(url),
    isAudio: ct.startsWith('audio/') || /\.(mp3|wav|ogg|aac)$/i.test(url),
    isText: ct.startsWith('text/') || /\.(txt|csv|md|json|xml)$/i.test(url),
  };
}

export function DocumentPreviewModal({ open, onClose, doc }) {
  const [expanded, setExpanded] = useState(false);

  if (!doc) return null;

  const fullUrl = resolveUrl(doc.file_url || doc.receipt_url);
  const { isImage, isPdf, isVideo, isAudio, isText } = detectType(fullUrl, doc.content_type);
  const hasPreview = fullUrl && (isImage || isPdf || isVideo || isAudio || isText);
  const displayName = doc.name || doc.file_name || 'Document';

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { onClose(); setExpanded(false); } }}>
      <DialogContent
        className={`bg-card border-border p-0 gap-0 transition-all duration-300 [&>button:last-child]:hidden ${
          expanded
            ? 'max-w-[95vw] w-[95vw] max-h-[95vh] h-[95vh]'
            : 'max-w-3xl w-full max-h-[85vh]'
        }`}
        data-testid="document-preview-modal"
      >
        {/* Header */}
        <DialogTitle className="sr-only">{displayName}</DialogTitle>
        <DialogDescription className="sr-only">Document preview dialog for {displayName}</DialogDescription>
        <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {isImage ? <Image className="h-4 w-4 text-primary shrink-0" /> :
             isPdf ? <FileText className="h-4 w-4 text-primary shrink-0" /> :
             isVideo ? <Film className="h-4 w-4 text-primary shrink-0" /> :
             <FileIcon className="h-4 w-4 text-primary shrink-0" />}
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate" data-testid="preview-modal-title">{displayName}</p>
              <div className="flex items-center gap-2">
                {doc.category && <Badge variant="outline" className="text-xs">{doc.category}</Badge>}
                {doc.file_name && doc.file_name !== displayName && <span className="text-xs text-muted-foreground truncate">{doc.file_name}</span>}
                {doc.file_size > 0 && <span className="text-xs text-muted-foreground">{formatSize(doc.file_size)}</span>}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1 shrink-0 ml-2">
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
              onClick={() => setExpanded(!expanded)}
              data-testid="preview-modal-expand"
            >
              {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
            {fullUrl && (
              <a href={fullUrl} target="_blank" rel="noopener noreferrer" download>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-foreground" data-testid="preview-modal-download">
                  <Download className="h-4 w-4" />
                </Button>
              </a>
            )}
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
              onClick={() => { onClose(); setExpanded(false); }}
              data-testid="preview-modal-close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className={`overflow-auto ${expanded ? 'flex-1' : 'max-h-[calc(85vh-56px)]'}`}>
          {!fullUrl ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
              <FileIcon className="h-16 w-16 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">No file attached to this document</p>
            </div>
          ) : isImage ? (
            <div className="flex items-center justify-center bg-black/10 p-2">
              <img
                src={fullUrl}
                alt={displayName}
                className={`object-contain ${expanded ? 'max-h-[calc(95vh-56px)]' : 'max-h-[calc(85vh-56px)]'} w-auto`}
                data-testid="preview-image"
              />
            </div>
          ) : isPdf ? (
            <iframe
              src={fullUrl}
              title={displayName}
              className={`w-full border-0 ${expanded ? 'h-[calc(95vh-56px)]' : 'h-[calc(85vh-56px)]'}`}
              data-testid="preview-pdf"
            />
          ) : isVideo ? (
            <div className="flex items-center justify-center bg-black/10 p-2">
              <video
                src={fullUrl}
                controls
                className={`w-full ${expanded ? 'max-h-[calc(95vh-56px)]' : 'max-h-[calc(85vh-56px)]'}`}
                data-testid="preview-video"
              />
            </div>
          ) : isAudio ? (
            <div className="flex flex-col items-center justify-center gap-4 py-16">
              <FileIcon className="h-16 w-16 text-muted-foreground/40" />
              <audio src={fullUrl} controls className="w-full max-w-lg" data-testid="preview-audio" />
            </div>
          ) : isText ? (
            <iframe
              src={fullUrl}
              title={displayName}
              className={`w-full border-0 bg-background ${expanded ? 'h-[calc(95vh-56px)]' : 'h-[calc(85vh-56px)]'}`}
              data-testid="preview-text"
            />
          ) : (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
              <FileIcon className="h-16 w-16 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">Preview not available for this file type</p>
              <a href={fullUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">
                Download to view
              </a>
            </div>
          )}
        </div>

        {/* Footer metadata */}
        {(doc.uploaded_by_name || doc.created_at || doc.expiration_date) && (
          <div className="flex items-center gap-4 px-4 py-2 border-t border-border text-xs text-muted-foreground shrink-0">
            {doc.uploaded_by_name && <span>Uploaded by {doc.uploaded_by_name}</span>}
            {doc.created_at && <span>{new Date(doc.created_at).toLocaleDateString()}</span>}
            {doc.expiration_date && <span>Expires: {doc.expiration_date}</span>}
            {doc.content_type && <span>{doc.content_type}</span>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
